import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  orgTypeSchema,
  type OrgType,
  type OrgTypeChange,
  type TenantDetail,
} from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { Btn, Card, CardHead, CardPad, Field, Note, Select } from '../admin-kit';

export const ORG_TYPES: readonly OrgType[] = orgTypeSchema.options;

/**
 * Het type organisatie van een bedrijf, in het detail van het platformbeheer (ORG-1, ADR-0086).
 *
 * Twee stappen, en dat is de hele opzet: eerst zegt de server wat er aan en uit zou gaan
 * (`dryRun`), pas na "Bevestigen" gebeurt het. Een type wijzigen zet modules uit bij een klant die
 * er misschien mee werkt; dat hoort niemand te doen zonder het overzicht gezien te hebben.
 */
export function OrgTypeCard({
  tenant,
  onChanged,
}: {
  tenant: TenantDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<OrgType>(tenant.orgType);
  const [preview, setPreview] = useState<OrgTypeChange | null>(null);

  const request = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const { data, error } = await api.PATCH('/api/v1/platform/tenants/{tenantId}/org-type', {
        params: { path: { tenantId: tenant.id } },
        body: { orgType: choice, dryRun },
      });
      if (error || !data) throw error ?? new Error('Wijzigen mislukt');
      return data as OrgTypeChange;
    },
    onSuccess: async (result) => {
      if (!result.applied) {
        setPreview(result);
        return;
      }
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
      onChanged();
    },
  });

  const flagLabel = (key: string) => t(`beheer.orgType.flags.${key.replace(/\./g, '_')}`, key);

  return (
    <Card>
      <CardHead title={t('beheer.orgType.title')} />
      <CardPad>
        <div className="a-stack">
          <p className="a-muted">{t('beheer.orgType.intro')}</p>
          <Field label={t('beheer.orgType.label')}>
            {(props) => (
              <Select
                {...props}
                data-testid="org-type-select"
                value={choice}
                onChange={(event) => {
                  setChoice(event.target.value as OrgType);
                  setPreview(null);
                }}
              >
                {ORG_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`beheer.orgType.types.${type}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {preview ? (
            <Note tone="info">
              <p>
                {t('beheer.orgType.preview', {
                  type: t(`beheer.orgType.types.${preview.orgType}`),
                })}
              </p>
              {preview.turnOn.length + preview.turnOff.length === 0 ? (
                <p>{t('beheer.orgType.nothing')}</p>
              ) : (
                <ul data-testid="org-type-preview">
                  {preview.turnOn.map((key) => (
                    <li key={key}>{t('beheer.orgType.turnOn', { flag: flagLabel(key) })}</li>
                  ))}
                  {preview.turnOff.map((key) => (
                    <li key={key}>{t('beheer.orgType.turnOff', { flag: flagLabel(key) })}</li>
                  ))}
                </ul>
              )}
              <p>{t('beheer.orgType.dataKept')}</p>
              {/* ORG-3: de systeemrollen volgen het nieuwe type ("Lid" in plaats van "Medewerker"). */}
              <p>
                {t('beheer.orgType.rolesRenamed', {
                  type: t(`beheer.orgType.types.${preview.orgType}`),
                })}
              </p>
            </Note>
          ) : null}

          {request.isError ? (
            <Note tone="danger" role="alert">
              {t('errors.generic')}
            </Note>
          ) : null}

          <div>
            {preview ? (
              <>
                <Btn variant="sec" onClick={() => setPreview(null)}>
                  {t('common.cancel')}
                </Btn>{' '}
                <Btn disabled={request.isPending} onClick={() => request.mutate(false)}>
                  {t('beheer.orgType.confirm')}
                </Btn>
              </>
            ) : (
              <Btn
                variant="sec"
                disabled={choice === tenant.orgType || request.isPending}
                onClick={() => request.mutate(true)}
              >
                {t('beheer.orgType.check')}
              </Btn>
            )}
          </div>
        </div>
      </CardPad>
    </Card>
  );
}
